// Geteilte CSV-Bausteine (Welle 2, #10) — Konventionen aus dem Reichweiten-Export:
// Komma-Separator, CRLF, RFC-4180-Quoting, Punkt-Dezimal (Excel-Import-kompatibel,
// kein Konflikt mit dem Komma-Separator). Kein BOM (Bestandsverhalten).
import { triggerBlobDownload } from "./download";

/**

 * Zeichen, mit denen Excel/LibreOffice eine Zelle als FORMEL lesen.
 *
 * Tabulator und Wagenruecklauf stehen bewusst NICHT in dieser Liste: sie waeren toter Code.
 * Der Vergleich unten laeuft auf dem per `trimStart()` bereinigten Wert — Leerraum,
 * Tabulator und Wagenruecklauf am Anfang sind dort bereits weg, und geprueft wird das
 * Zeichen DAHINTER. `"\t=1+1"` wird also entschaerft, ohne dass `\t` hier stehen muss.
 * (Befund von Reviewer 2: die frueheren Eintraege konnten nie greifen und widersprachen
 * ihrem eigenen Kommentar.)
 */
const FORMEL_START = ["=", "+", "-", "@"];

/**
 * Echte Zahlen, die nicht entschaerft werden duerfen.
 *
 * Bewusst GROSSZUEGIG: erlaubt Punkt- und Komma-Dezimale, Tausender-Trennzeichen und
 * Exponenten (`-5`, `-1234.50`, `-1,5`, `-1.234,50`, `1e3`). Das ist sicher, weil eine
 * Formel nie wie eine Zahl aussieht — Excel liest `=`/`@` als Anfang, und was diesem Muster
 * entspricht, kann keine Formel sein. Zu ENG waere dagegen schaedlich: die vier bestehenden
 * Exporte reichen freie Zeichenketten durch, und eine als Text markierte Zahl zerstoert im
 * Tabellenprogramm jede Summe. (Befund von Reviewer 1, angenommen.)
 */
const ZAHL = /^[+-]?(\d{1,3}([.,]\d{3})+([.,]\d+)?|\d+([.,]\d+)?|[.,]\d+)([eE][+-]?\d+)?$/;

/**
 * Entschaerft eine Zelle, die ein Tabellenprogramm sonst als Formel ausfuehren wuerde
 * (Modul 38, Koordinator-Auftrag 2026-08-17).
 *
 * Warum das hier steht und nicht beim Aufrufer: dieser Baustein bedient ALLE CSV-Exporte
 * der Oberflaeche (Reichweite, Uebersicht, Artikel-Analyse, Einkauf). Die Werte darin
 * stammen aus Fremdquellen — DR-Stammdaten, Notizen, Lieferantentexte. Ein Artikelname wie
 * `=HYPERLINK("http://…";"Rechnung")` oder `@SUM(A1)` ist beim Oeffnen der Datei kein Text
 * mehr, sondern ein Programm auf dem Rechner des Einkaufs. Ein fuehrendes `'` macht daraus
 * wieder Text; die Zelle zeigt denselben Inhalt.
 *
 * ZAHLEN BLEIBEN ZAHLEN. `-5` oder `-1234.50` sind keine Formeln, sondern Werte — sie mit
 * `'` zu versehen wuerde jede Summenbildung im Tabellenprogramm zerstoeren und aus einem
 * Schutz einen Schaden machen. Deshalb die Ausnahme fuer wohlgeformte Zahlen; `+49 170`
 * (Telefonnummer) ist keine und wird entschaerft.
 *
 * Additiv: Trennzeichen, Quoting-Regel, CRLF und das Fehlen des BOM bleiben unveraendert.
 */
export function neutralizeCsvFormula(value: string): string {
  const geprueft = value.trimStart();
  if (geprueft.length === 0) return value;
  if (!FORMEL_START.includes(geprueft[0]!)) return value;
  // Fuer die Zahl-Ausnahme beidseitig trimmen: `-5 ` aus einer Tabellenspalte ist eine Zahl
  // mit Leerzeichen, keine Formel. (Reviewer 1.)
  if (ZAHL.test(geprueft.trimEnd())) return value;
  return `'${value}`;
}

type CsvSeparator = "," | ";";

/** Zeichen, die eine Zelle quoten muessen — abhaengig vom Separator. */
const MUSS_QUOTEN: Record<CsvSeparator, RegExp> = {
  ",": /[",\r\n]/,
  ";": /[";\r\n]/,
};

/** Gemeinsamer Kern fuer Komma- und Semikolon-CSV (Formel-Schutz + RFC-4180-Quoting). */
function escapeCsvFieldWith(value: string, separator: CsvSeparator): string {
  const sicher = neutralizeCsvFormula(value);
  if (MUSS_QUOTEN[separator].test(sicher)) {
    return `"${sicher.replaceAll('"', '""')}"`;
  }
  return sicher;
}

export function escapeCsvField(value: string): string {
  return escapeCsvFieldWith(value, ",");
}

export function formatCsvDecimal(n: number): string {
  return n.toFixed(2);
}

export function buildCsv(header: string[], rows: string[][]): string {
  const lines = [header.map(escapeCsvField).join(",")];
  for (const row of rows) {
    lines.push(row.map(escapeCsvField).join(","));
  }
  return lines.join("\r\n");
}

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  triggerBlobDownload(blob, filename);
}

// ------------------------------------------------------------
//  Deutsches Excel (Verkaufspreis, Task 7): Semikolon-Separator, Dezimalkomma, UTF-8-BOM.
//  Excel mit deutscher Gebietsschema-Einstellung liest Komma als Dezimalzeichen und
//  Semikolon als Listentrenner; ohne BOM zeigt es Umlaute falsch. Die Komma-Bausteine oben
//  bleiben unveraendert (Bestandsexporte).
// ------------------------------------------------------------

/** `1234.5` -> `"1234,50"`: immer 2 Nachkommastellen, Dezimalkomma, kein Tausendertrenner. */
export function formatCsvDecimalDe(n: number): string {
  return n.toFixed(2).replace(".", ",");
}

/** Semikolon-CSV mit CRLF nach jeder Zeile; Quoting und Formel-Schutz wie `escapeCsvField`. */
export function buildCsvDe(header: string[], rows: string[][]): string {
  const zeile = (felder: string[]) => felder.map((f) => escapeCsvFieldWith(f, ";")).join(";");
  return [zeile(header), ...rows.map(zeile)].map((l) => `${l}\r\n`).join("");
}

/** Download mit vorangestelltem UTF-8-BOM, damit Excel die Datei als UTF-8 erkennt. */
export function downloadCsvDe(filename: string, csv: string): void {
  const blob = new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" });
  triggerBlobDownload(blob, filename);
}

